import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Formik, Form } from 'formik';
import type { LicenseCheckContextType } from 'src/web/license/LicenseCheckContext';
import useProductName from 'brand-customization/useProductName';

import FormikInput from 'components/common/FormikInput';
import ModalSubmit from 'components/common/ModalSubmit';
import DisclaimerModalContent from 'security-app/components/Investigations/AIReport/DisclaimerModalContent';
import Modal from 'components/bootstrap/Modal';
import { IconButton } from 'components/common';
import type { WidgetMenuActionComponentProps } from 'views/components/widgets/Types';
import useAssistantSidebar from 'ai/sidebar/useAssistantSidebar';
import useWidgetResults from 'views/components/useWidgetResults';
import useView from 'views/hooks/useView';
import createWidgetSummaryRequest from 'ai/widget-summary/createWidgetSummaryRequest';
import useAITermsAcceptance from 'ai/hooks/useAITermsAcceptance';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import usePersistedSetting from 'hooks/usePersistedSetting';
import { updateWidget } from 'views/logic/slices/widgetActions';
import useViewsDispatch from 'views/stores/useViewsDispatch';
import type Widget from 'views/logic/widgets/Widget';
import SaveContextInfo from 'ai/common/SaveContextInfo';
import useLicenseCheck from 'license/useLicenseCheck';
import useAIConfiguration from 'ai/hooks/useAIConfiguration';

const StyledIconButton = styled(IconButton)`
  span {
    position: relative;
    left: -1px;
  }
`;
const useDoNotAskAgain = () => usePersistedSetting('views.widgetSummary.doNotAskAgain');

const telemetryEvents = {
  cancel: TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.TERMS_AND_CONDITIONS_CANCELED,
  consent: TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.TERMS_AND_CONDITIONS_CONSENT,
};

type FormState = {
  context: string;
  description: string;
  doNotAsk?: boolean;
};

const checkPrerequisites = (
  license: LicenseCheckContextType,
  loadingAcceptance: boolean,
  widget: Widget,
  widgetResult: unknown,
  productName: string,
  aiFeaturesEnabled: boolean,
  termsAcceptanceError: string | undefined,
) => {
  const invalidLicense = !license?.security?.isValid && !license?.enterprise?.isValid;
  const widgetEmpty = widget?.config?.isEmpty;
  const noData = (widgetResult as { chart: { total: number } })?.chart?.total === 0;
  const actionDisabled =
    invalidLicense || loadingAcceptance || widgetEmpty || noData || !aiFeaturesEnabled || termsAcceptanceError;

  let disabledReason = '';
  if (termsAcceptanceError) {
    disabledReason = `Error fetching terms: ${termsAcceptanceError}`;
  } else if (!aiFeaturesEnabled) {
    disabledReason = 'AI features are disabled by the administrator.';
  } else if (invalidLicense) {
    disabledReason = `No valid ${productName} Enterprise or Security license.`;
  } else if (loadingAcceptance) {
    disabledReason = 'Loading terms status ...';
  } else if (widgetEmpty) {
    disabledReason = 'Widget is empty.';
  } else if (noData) {
    disabledReason = 'No data to analyze.';
  }

  return { actionDisabled, disabledReason };
};

const WidgetSummaryActionComponent = ({ widget }: WidgetMenuActionComponentProps) => {
  const license = useLicenseCheck();
  const productName = useProductName();
  const { generate } = useAssistantSidebar();
  const { widgetData: widgetResult } = useWidgetResults(widget.id);
  const view = useView();
  const generateForWidget = useCallback(
    (_widget: Widget) => generate(createWidgetSummaryRequest(view, _widget, widgetResult)),
    [generate, view, widgetResult],
  );
  const [requiresContext, setRequiresContext] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState(false);
  const {
    data: { accepted, error: termsAcceptanceError },
    isLoading: loadingAcceptance,
    refetch,
  } = useAITermsAcceptance();
  const [doNotAskAgain, setDoNotAskAgain] = useDoNotAskAgain();
  const dispatch = useViewsDispatch();
  const { data: aiConfig, isLoading: loadingAIConfig } = useAIConfiguration();
  const aiFeaturesEnabled = useMemo(
    () => !loadingAIConfig && aiConfig?.isEnabled === true && !termsAcceptanceError,
    [aiConfig?.isEnabled, loadingAIConfig, termsAcceptanceError],
  );

  const onClick = useCallback(() => {
    if (!accepted) {
      setRequiresConsent(true);
    }

    const widgetRequiresContext = !widget.description || !widget.context;
    if (widgetRequiresContext && !doNotAskAgain) {
      setRequiresContext(true);
    }

    if (accepted && (!widgetRequiresContext || doNotAskAgain)) {
      generateForWidget(widget);
    }
  }, [accepted, doNotAskAgain, generateForWidget, widget]);

  const onConsent = useCallback(() => {
    setRequiresConsent(false);
    refetch();
  }, [refetch]);

  const clearRequestWaitingForConsent = useCallback(() => {
    setRequiresConsent(false);
    setRequiresContext(false);
  }, []);
  const modal = useMemo(() => {
    if (requiresConsent) {
      return (
        <Modal show onHide={clearRequestWaitingForConsent} bsSize="large">
          <DisclaimerModalContent
            onClose={clearRequestWaitingForConsent}
            onConsent={onConsent}
            telemetryEvents={telemetryEvents}
          />
        </Modal>
      );
    }
    if (requiresContext) {
      const close = () => setRequiresContext(false);
      const initialValues = {
        context: widget?.context ?? '',
        description: widget?.description ?? '',
        doNotAsk: doNotAskAgain ?? false,
      };
      const onSave = async (values: FormState) => {
        if (values.doNotAsk !== doNotAskAgain) {
          setDoNotAskAgain(values.doNotAsk);
        }
        if (values.context !== initialValues.context || values.description !== initialValues.description) {
          const newWidget = widget.toBuilder().description(values.description).context(values.context).build();
          await dispatch(updateWidget(widget.id, newWidget));
          generateForWidget(newWidget);
        } else {
          generateForWidget(widget);
        }
        close();
      };

      return (
        <Modal show onHide={close}>
          <Modal.Header>
            <h3>Adding more context to Widget</h3>
          </Modal.Header>
          <Modal.Body>
            <Formik<FormState> initialValues={initialValues} onSubmit={onSave}>
              <Form>
                <p>
                  Adding a clear description and relevant context helps clarify your widget&#39;s purpose and guides
                  users on interpreting its results. This ensures the widget summary feature delivers the most accurate
                  and insightful analysis possible. Would you like to add one now?
                </p>

                <FormikInput label="Description" name="description" id="description" />
                <FormikInput label="Context" name="context" type="textarea" id="context" rows={12} />
                <FormikInput label="Do not ask me again" name="doNotAsk" type="checkbox" id="doNotAsk" />
                <SaveContextInfo />
                <ModalSubmit submitButtonText="Run" displayCancel={false} />
              </Form>
            </Formik>
          </Modal.Body>
        </Modal>
      );
    }

    return null;
  }, [
    clearRequestWaitingForConsent,
    dispatch,
    doNotAskAgain,
    generateForWidget,
    onConsent,
    requiresConsent,
    requiresContext,
    setDoNotAskAgain,
    widget,
  ]);

  const { actionDisabled, disabledReason } = useMemo(
    () =>
      checkPrerequisites(
        license,
        loadingAcceptance,
        widget,
        widgetResult,
        productName,
        aiFeaturesEnabled,
        termsAcceptanceError,
      ),
    [license, loadingAcceptance, widget, widgetResult, productName, aiFeaturesEnabled, termsAcceptanceError],
  );

  return (
    <>
      <StyledIconButton
        disabled={actionDisabled}
        name="note_add"
        iconType="regular"
        title={`Generate Widget Summary${actionDisabled ? ` - Disabled: ${disabledReason}` : ''}`}
        onClick={onClick}
      />
      {modal}
    </>
  );
};
export default WidgetSummaryActionComponent;
